import * as React from 'react';
import './calendar.css';
import Calendar from '@fullcalendar/react';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import dayGridPlugin from '@fullcalendar/daygrid';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import plLocale from '@fullcalendar/core/locales/pl';
import { useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import { StyledCalendar } from './styles';
import SaleCalendarToolbar from './SaleCalendarToolbar';
import { useSaleCalendar } from './SaleCalendarContext';
import { PeriodContextMenu } from './context-menu/PeriodContextMenu';
import { DayContextMenu } from './context-menu/DayContextMenu';
import { setExternalOrderId } from '../../../services/redux-actions';
import { useDispatch, useSelector } from 'react-redux';
import { EventContextMenu } from './context-menu/EventContextMenu';
import StateColorLegend from './StateColorLegend';
import PackagesLegend from './PackagesLegend';

export default function SaleCalendarView() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    events,
    periods,
    summaryEvents,
    periodsWithSales,
    calendarRef,
    loading,
    contextMenu,
    blockedResourceDates,
    customNoteEvents,
    transitionDayEvents,
    //
    view,
    date,
    //
    onDatePrev,
    onDateNext,
    onDateToday,
    onChangeView,
    onChangeUser,
    onSelectRange,
    onInitialView
  } = useSaleCalendar();
  const { seasonYear } = useSelector(state => state.app);
  const [eventContextMenu, setEventContextMenu] = React.useState();
  const [fiveDaysId, setFiveDaysId] = React.useState();

  React.useEffect(() => {
    if (periodsWithSales.length > 0) {
      setFiveDaysId(periods.find(p => ['5 DNI', '5 DNI GIEWARTÓW', '5 DNI MRÓWKI'].includes(p.title)).id);
    }
  }, [periods]);

  const handleEventClick = e => {
    if (e.event?.extendedProps?.crm_record === false) {
      dispatch(setExternalOrderId(e.event.id));
      document.querySelectorAll('.custom-tooltip').forEach(t => t.remove());
      navigate('/employee/orders');
    } else if (e.event?.extendedProps?.crm_record === true) {
      window.open(`${process.env.REACT_APP_SCENERIO_CRM_URL}employee/submitted-forms?id=${e.event.id}`);
    }
  };

  function addTooltip(el, event) {
    const tooltip = document.createElement('div');
    const tooltipData = event.extendedProps.noteContent || event.extendedProps.tooltip_text;

    tooltip.innerHTML = typeof tooltipData === 'object'
      ? Object.entries(tooltipData).map(([key, value]) => {
        const val = typeof value === 'string' && value.startsWith('http')
          ? `<a href="${value}" target="_blank" style="color: #90caf9;">${value}</a>`
          : String(value);

        return `<div><strong>${key}:</strong> ${val}</div>`;
      })
        .join('')
      : String(tooltipData);

    Object.assign(tooltip.style, {
      position: 'absolute',
      background: 'rgba(97, 97, 97, 0.92)',
      color: '#fff',
      padding: '6px 10px',
      borderRadius: '4px',
      fontSize: '0.95rem',
      fontFamily: 'inherit',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      pointerEvents: 'auto',
      zIndex: 9999,
      display: 'none',
      maxWidth: '520px',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      transition: 'opacity 0.15s ease-in-out',
      opacity: '0'
    });

    document.body.appendChild(tooltip);
    let hideTimeout = null;

    const updatePosition = e => {
      const offsetX = 12;
      const offsetY = 12;
      const tooltipRect = tooltip.getBoundingClientRect();
      let left = e.pageX + offsetX;
      let top = e.pageY + offsetY;

      // Ogranicz w poziomie
      if (left + tooltipRect.width > window.innerWidth - 10) {
        left = window.innerWidth - tooltipRect.width - 10;
      }

      // Ogranicz w pionie
      if (top + tooltipRect.height > window.innerHeight - 10) {
        top = window.innerHeight - tooltipRect.height - 10;
      }

      tooltip.style.left = `${Math.max(0, left)}px`;
      tooltip.style.top = `${Math.max(0, top)}px`;
    };

    const showTooltip = e => {
      clearTimeout(hideTimeout);
      updatePosition(e);
      tooltip.style.display = 'block';
      requestAnimationFrame(() => {
        tooltip.style.opacity = '1';
      });
    };

    const hideTooltip = () => {
      hideTimeout = setTimeout(() => {
        tooltip.style.opacity = '0';
        setTimeout(() => {
          tooltip.style.display = 'none';
        }, 50);
      }, 80);
    };

    const forceHideTooltip = () => {
      clearTimeout(hideTimeout);
      tooltip.style.opacity = '0';
      tooltip.style.display = 'none';
    };

    el.addEventListener('mouseenter', showTooltip);
    el.addEventListener('mousemove', updatePosition);
    el.addEventListener('mouseleave', hideTooltip);
    el.addEventListener('contextmenu', () => {
      forceHideTooltip();
    });

    tooltip.addEventListener('mouseenter', () => clearTimeout(hideTimeout));
    tooltip.addEventListener('mouseleave', hideTooltip);
  }

  const handleRightClick = (calendarEvent, event) => {
    setEventContextMenu({ calendarEvent, event });
  };

  const resourceLabelDidMountHandler = ({ el, resource }) => {
    if (resource.extendedProps?.type === 'summary') {
      const fiveDaysId = periods.find(p => ['5 DNI', '5 DNI GIEWARTÓW', '5 DNI MRÓWKI'].includes(p.title)).id;
      const oneDayId = periods.find(p => p.title == '1 DZIEŃ').id;
      if (resource.getParent()?.id == fiveDaysId && resource.title == 'Zostało do sprzedania') {
        el.classList.add('resource-summary-row-5-days');
      } else if (resource.getParent()?.id == oneDayId && resource.title == 'Zostało do sprzedania') {
        el.classList.add('resource-summary-row-5-days');
      } else {
        el.classList.add('resource-summary-row');
      }
    }
    el.style.fontWeight = '700';

    ['shared_capacity_top', 'shared_capacity_bottom'].forEach(key => {
      if (resource.extendedProps?.[key]) {
        el.classList.add(`resource-${key.replace('_', '-')}`);
        const timelineLane = document.querySelector(`.fc-timeline-lane[data-resource-id="${resource.id}"]`);
        if (timelineLane) {
          timelineLane.classList.add(`resource-${key.replace('_', '-')}`);
        }
      }
    });
  };

  const eventDidMountHandler = ({ el, event }) => {
    if (event.extendedProps?.noteContent || event.extendedProps?.tooltip_text) {
      addTooltip(el, event);
    }
    if (event.extendedProps?.type === 'summary') {
      el.style.borderRadius = '0';
      el.style.textAlign = 'center';
      el.style.borderRight = '1px solid rgba(22, 28, 36, 0.36)';
    }

    const resource = event.getResources()[0] ?? null;
    if (
      resource &&
      event.extendedProps.type === 'summary' &&
      resource?.getParent()?.id == fiveDaysId &&
      resource.title === 'Zostało do sprzedania'
    ) {
      // dodaj klasę do <td>
      el.closest('td')?.classList.add('resource-summary-cell-5-days');
    }
    el.addEventListener('contextmenu', e => {
      e.preventDefault();
      handleRightClick(event, e);
    });
  };

  const slotLaneDidMountHandler = arg => {
    arg.el.addEventListener('contextmenu', e => {
      const mouseY = e.clientY;
      let resourceId = null;

      if (mouseY != null) {
        const rows = Array.from(document.querySelectorAll('.fc-datagrid-body tr'));
        for (const row of rows) {
          const rect = row.getBoundingClientRect();
          if (mouseY >= rect.top && mouseY <= rect.bottom) {
            const td = row.querySelector('[data-resource-id]');
            if (td) {
              resourceId = td.getAttribute('data-resource-id');
            }
            break;
          }
        }
      }
      e.preventDefault();
      handleRightClick({ ...arg, resourceId }, e);
    });
  };

  const eventContentRenderer = ({ event, el }) => <CustomEvent el={el} event={event} fiveDaysId={fiveDaysId} />;

  const calendarPlugins = [listPlugin, dayGridPlugin, interactionPlugin, resourceTimelinePlugin];

  const calendarViews = {
    resourceTimelineDay: {
      type: 'resourceTimeline',
      slotDuration: { days: 1 }
    },
    resourceTimelineWeek: {
      type: 'resourceTimeline',
      slotDuration: { days: 1 },
      slotLabelFormat: [
        { weekday: 'long' },
        { day: 'numeric', month: 'long' }
      ]
    },
    resourceTimelineMonth: {
      type: 'resourceTimeline',
      slotDuration: { days: 1 },
      slotMinWidth: 150,
      slotLabelFormat: [
        { weekday: 'short' },
        { day: 'numeric', month: 'numeric' }
      ]
    }
  };

  const slotLabelFormat = [
    { weekday: 'long' },
    { day: 'numeric', month: 'long' }
  ];


  React.useEffect(() => {
    onInitialView();
  }, [onInitialView]);

  console.log(events);

  return (
    <>
      <Container maxWidth={false}>
        {loading && <div className="overlay">Wczytywanie...</div>}
        <Card>
          <StyledCalendar>
            <SaleCalendarToolbar
              date={date}
              view={view}
              loading={false}
              onNextDate={onDateNext}
              onPrevDate={onDatePrev}
              onToday={onDateToday}
              onChangeView={onChangeView}
              onChangeUser={onChangeUser}
            />
            <Calendar
              ref={calendarRef}

              // Data & view
              initialDate={date}
              initialView={view}
              height="100%"
              contentHeight="auto"
              scrollTimeReset={false}
              locale={plLocale}
              plugins={calendarPlugins}
              views={calendarViews}
              slotLabelFormat={slotLabelFormat}
              validRange={{
                start: `${seasonYear.toString()}-01-01`,
                end: `${(seasonYear + 1).toString()}-01-01`
              }}

              // Edycja, zaznaczanie, widoczność
              weekends
              editable={false}
              eventStartEditable={false}
              eventDurationEditable={false}
              selectable
              rerenderDelay={100}
              allDayMaintainDuration

              // Terminy
              resources={periodsWithSales}
              resourceOrder="order"
              resourceAreaHeaderContent="Termin"
              resourceLabelDidMount={resourceLabelDidMountHandler}
              resourceAreaWidth="300px"

              // Eventy
              events={[...events, ...summaryEvents, ...blockedResourceDates, ...customNoteEvents, ...transitionDayEvents]}
              eventClick={handleEventClick}
              eventContent={eventContentRenderer}
              eventDidMount={eventDidMountHandler}
              dayMaxEventRows={view === 'resourceTimelineMonth' ? 4 : false}
              eventDisplay="block"
              eventOrder={(a, b) => {
                const aCreated = new Date(a.extendedProps.created_at);
                const bCreated = new Date(b.extendedProps.created_at);

                return bCreated - aCreated; // najnowsze wyżej
              }}
              // Sloty
              slotLaneDidMount={slotLaneDidMountHandler}
              slotMinWidth={100}
              // Toolbar
              headerToolbar={false}

              // Callbacki
              select={onSelectRange}
            />
          </StyledCalendar>
        </Card>
        <StateColorLegend />
        <PackagesLegend />
      </Container>
      {contextMenu.visible && contextMenu.contextType == 'periodContextMenu'
        ? <PeriodContextMenu />
        : contextMenu.visible && contextMenu.colspan && contextMenu.contextType == 'dayContextMenu'
          ? <DayContextMenu />
          : null
      }
      {eventContextMenu && <EventContextMenu eventContextMenu={eventContextMenu} handleClose={() => setEventContextMenu()} />}

    </>
  );
}

function CustomEvent({ el, event, fiveDaysId }) {
  const isCrmRecord = event.extendedProps?.crm_record;
  const hasPackage = event.extendedProps?.package_name;


  function getClassName () {
    if (event.extendedProps.type === 'summary') {
      const resource = event.getResources()[0] ?? null;
      if (
        resource &&
        event.extendedProps.type === 'summary' &&
        resource?.getParent()?.id == fiveDaysId &&
        resource.title === 'Zostało do sprzedania'
      ) {
        return 'resource-summary-row-no-background';
      }

      return 'resource-summary-row';
    }

    return '';
  }

  return (
    <div className="fc-event-main-frame" style={{ position: 'relative' }}>
      {isCrmRecord && (
        <span
          style={{
            position: 'absolute',
            top: -5,
            right: -5,
            backgroundColor: 'black',
            color: 'white',
            fontWeight: 700,
            fontSize: '0.6rem',
            padding: '1px 4px',
            borderRadius: '3px',
            opacity: 0.9,
            zIndex: 10
          }}
        >
          CRM
        </span>
      )}
      {hasPackage && (
        <span
          style={{
            position: 'absolute',
            top: isCrmRecord ? 10 : -5,
            right: -5,
            backgroundColor: 'brown',
            color: 'white',
            fontWeight: 700,
            fontSize: '0.6rem',
            padding: '1px 4px',
            borderRadius: '3px',
            opacity: 0.9,
            zIndex: 10
          }}
        >
          {event.extendedProps.package_calendar_shortcut}
        </span>
      )}
      <div className="fc-event-title-container">
        <div className="fc-event-title fc-sticky">
          <span
            className={getClassName()}
            dangerouslySetInnerHTML={{ __html: event.title }}
          />
        </div>
      </div>
    </div>
  );
}

